// src/hooks/use-internal-project-tasks.ts
// VIB-1208: oppgaver på internprosjekter. Ingen ny tabell — en "oppgave" er
// en budget_lines-rad på et internprosjekt (cost_type='internal',
// phase='production' — gjenbruker eksisterende fase-enum, jf. Erik 15.07:
// "fase=prod som default, unngår ny fase-enum"). Eksisterende kobling
// time_entries.budget_line_id og string-matching-format virker rett ut av
// boksen så snart en oppgave er valgt ved føring.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { MyTask } from "./use-my-tasks";

export interface InternalProjectTask {
  id: string; // budget_line id — dobler som MyTask.budget_line_id
  project_id: string;
  project_name: string;
  description: string;
}

interface BudgetLineWithProjectRow {
  id: string;
  description: string | null;
  project_id: string | null;
  projects: { name: string } | null;
}

/**
 * Alle oppgaver (budget_lines) på tvers av AKTIVE internprosjekter. Synlig
 * for alle ansatte (interne prosjekter er allerede åpne for alle, jf.
 * use-my-tasks sin internalProjects — ingen nye tilgangsbegrensninger).
 *
 * Bundet lesning: filtrert til projects.is_internal — antall internprosjekter
 * og deres oppgaver er en liten, avgrenset mengde, IKKE et team-bredt
 * ubundet read av budget_lines (VIB-1249-paginerings-fellen). `.limit(500)`
 * er en eksplisitt safety-net, ikke en forventet grense i praksis.
 */
export function useInternalProjectTasks() {
  return useQuery({
    queryKey: ["internal-project-tasks"],
    queryFn: async (): Promise<InternalProjectTask[]> => {
      const { data, error } = await supabase
        .from("budget_lines")
        .select("id, description, project_id, projects!inner(name, is_internal, is_active, deleted_at)")
        .eq("cost_type", "internal")
        .eq("projects.is_internal", true)
        .eq("projects.is_active", true)
        .is("projects.deleted_at", null)
        .order("description")
        .limit(500);
      if (error) throw error;
      return ((data ?? []) as unknown as BudgetLineWithProjectRow[])
        .filter((row) => !!row.project_id && !!row.projects)
        .map((row) => ({
          id: row.id,
          project_id: row.project_id as string,
          project_name: row.projects?.name ?? "—",
          description: row.description ?? "—",
        }));
    },
    staleTime: 2 * 60 * 1000,
  });
}

/** Shape en intern oppgave til en MyTask-kompatibel oppføring for pickere
 * (NewRegistrationPopup, RegistrationEditDialog) og TimeTrackingPopup.
 *
 * `phase: "intern"` er IKKE en gyldig project_phase-DB-verdi — den brukes
 * kun klient-side for task_description-strengbygging og fase-etikett-oppslag
 * i pickerne (lokale PHASE_LABELS-records), aldri skrevet tilbake som
 * budget_lines.phase (den er 'production' i DB, jf. Erik 15.07-defaulten).
 */
export function internalTaskToMyTask(task: InternalProjectTask): MyTask {
  return {
    id: task.id,
    budget_line_id: task.id,
    project_id: task.project_id,
    task_description: task.description,
    task_status: "in_progress",
    phase: "intern",
    project_name: task.project_name,
    assigned_hours: 0,
    logged_hours: 0,
    is_internal: true,
  };
}

interface CreateInternalTaskInput {
  project_id: string;
  description: string;
}

/**
 * Oppretter en ny intern oppgave (budget_lines-rad). RLS-policy
 * budget_lines_role_write (20260718132951_internal_project_tasks_rls.sql)
 * tillater ALLE innloggede å skrive på internprosjekter — konsistent med
 * Erik 15.07: "alle ansatte kan opprette".
 *
 * Audit: DB-triggeren log_budget_line_inserted (allerede på plass, VIB-1184)
 * logger budget_line_added i project_events med current_user_id() som
 * aktør automatisk — ingen egen logEvent-kode nødvendig her (verifisert mot
 * 20260624170402_budget_line_created_by_actor.sql).
 */
export function useCreateInternalProjectTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ project_id, description }: CreateInternalTaskInput) => {
      const trimmed = description.trim();
      if (!trimmed) throw new Error("Oppgavenavn kan ikke være tomt");
      const { data, error } = await supabase
        .from("budget_lines")
        .insert({
          project_id,
          description: trimmed,
          phase: "production",
          cost_type: "internal",
          amount: 0,
          quantity_hours: 0,
        })
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.project_id });
      toast.success("Oppgave opprettet");
    },
    onError: (err) => {
      toast.error(`Kunne ikke opprette oppgave: ${(err as Error).message}`);
    },
  });
}
